/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import session from '../../../../share/session';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import errors from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  WAIT: 'wait',
  EXECUTE: 'execute',
  IG_OFF_INSTRUCTION: 'igOffInstruction',
  SUCCESS: 'success',
  ERROR: 'error',
  UNEXPECTED_ERROR: 'unexpectedError',
};

export default {
  name: 'requirement145',
  data() {
    return {
      phase: PHASE.WAIT,
      isLoading: false,
      modal_requirement_145: false,
      isDispose: false,
      systemId: ''
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     * @returns {string} ヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
        case PHASE.UNEXPECTED_ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.WAIT:
        case PHASE.EXECUTE:
        case PHASE.IG_OFF_INSTRUCTION:
        default:
          return 'secondary';
      }
    },

    /**
     * 表示ヘッダー文字列判定
     * @returns {string} ヘッダー文字列
     */
    headerTitle() {
      // 待機表示, IG OFF指示表示
      const confirmationTitle = 'message.common_title_confirmation';
      // 実行中表示
      const executionTitle = 'individual.common_title_execution';
      // 停止表示
      const successTitle = 'message.common_title_success';
      // エラー表示,  共通エラー
      const errorTitle = 'message.common_title_error';
      const titleMap = {
        [PHASE.WAIT]: confirmationTitle,
        [PHASE.EXECUTE]: executionTitle,
        [PHASE.IG_OFF_INSTRUCTION]: confirmationTitle,
        [PHASE.SUCCESS]: successTitle,
        [PHASE.ERROR]: errorTitle,
        [PHASE.UNEXPECTED_ERROR]: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    /**
     * 作業サポート表示メッセージ判定
     * @returns {string} メッセージ
     */
    workSupportMessage() {
      const messageMap = {
        [PHASE.WAIT]: `${this.i18nWsKey}.REQ_WS_145_TXT_1`,
        [PHASE.EXECUTE]: `${this.i18nWsKey}.REQ_WS_145_TXT_2`,
        [PHASE.IG_OFF_INSTRUCTION]: `${this.i18nWsKey}.REQ_WS_145_TXT_3`,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_145_TXT_4`,
        [PHASE.ERROR]: `${this.i18nWsKey}.REQ_WS_145_TXT_5`,
        [PHASE.UNEXPECTED_ERROR]: 'individual.error_communication_error',
      };
      return this.$t(messageMap[this.phase]);
    },

    /**
     * [OK]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.WAIT]: true,
        [PHASE.IG_OFF_INSTRUCTION]: true,
        [PHASE.SUCCESS]: true,
        [PHASE.ERROR]: true,
        [PHASE.UNEXPECTED_ERROR]: true,
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * [キャンセル]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.WAIT]: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_145 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /**
     * [OK]ボタンクリック処理
     */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.WAIT:
          this.phase = PHASE.EXECUTE;
          await this.executeProcess();
          break;
        case PHASE.IG_OFF_INSTRUCTION:
          await this.igOffProcess();
          break;
        case PHASE.SUCCESS:
        case PHASE.ERROR:
          this.closeWizard();
          break;
        case PHASE.UNEXPECTED_ERROR:
          await this.exitProcess(true);
          break;
        default:
          break;
      }
    },

    /**
     * [キャンセル]ボタンクリック処理
     */
    onCancelClicked() {
      switch (this.phase) {
        case PHASE.WAIT:
          this.closeWizard();
          break;
        default:
          break;
      }
    },

    /** 実行処理(実行中表示) */
    async executeProcess() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'write'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了：IG OFF指示表示へ遷移
            this.phase = PHASE.IG_OFF_INSTRUCTION;
            break;
          case REQUEST_STATUS_FAILED:
            // 異常終了：エラー画面へ遷移
            // is_dispose を保持しておく
            this.isDispose = Boolean(response.data?.user_data?.is_dispose);
            await this.errorProcess();
            break;
          default:
            // その他異常終了：共通エラー画面へ遷移
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** IG OFF指示画面の「OK」ボタン押下 */
    async igOffProcess() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            switch (response.data.user_data.res_type) {
              case 'ok':
                // データ読出しの結果が 0x00 の場合停止画面へ遷移
                await this.dispose();
                this.phase = PHASE.SUCCESS;
                break;
              case 'ng':
              default:
                // データ読出しの結果が 0x00以外 の場合エラー画面へ遷移
                await this.errorProcess();
                break;
            }
            break;
          case REQUEST_STATUS_FAILED:
            // 異常終了
            switch (response.data.async_code) {
              case errors.WSP_145_READ_FAILED:
                // データ読出しで異常終了の場合エラー画面へ遷移
                await this.errorProcess();
                break;
              case errors.WSP_145_SECURITY_FAILED:
              default:
                // 二段階認証で異常終了の場合共通エラー画面へ遷移
                this.showUnexpectedError();
                break;
            }
            break;
          default:
            // その他異常終了：共通エラー画面へ遷移
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** エラー時の処理 */
    async errorProcess() {
      if (this.isDispose) {
        // 現在セッションがdefaultSession
        await this.dispose();
      } else {
        // 現在セッションがdefaultSessionではない
        await this.exitProcess(false);
      }
      if (this.phase !== PHASE.UNEXPECTED_ERROR) {
        // API実行中に共通エラーに遷移した場合はエラー画面に遷移しない
        this.phase = PHASE.ERROR;
      }
    },

    /**
     * 終了処理
     * @param {boolean} isUnexpectedError 共通エラーから遷移しているかどうか
    */
    async exitProcess(isUnexpectedError) {
      try {
        this.isLoading = true;
        let isError = false;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; isError = true; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (isUnexpectedError || !isError) {
          // 結果に関わらずdispose処理を実施する
          await this.dispose();
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (this.phase === PHASE.UNEXPECTED_ERROR) {
          // 共通エラーから呼ばれた場合は機能終了する
          this.closeWizard();
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.UNEXPECTED_ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_145 = false;
    },
  },

  mounted() {
    this.init();
  },
};